import { type ComponentType, Suspense } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
  ScrollRestoration,
  useLocation,
  useMatches,
} from 'react-router';
import { shellCopy } from '@/copy/shell';
import { Header } from './Header';
import { Spinner } from './kit';
import type { ModuleRoute, TabId } from './module';
import { moduleRoutes } from './registry';
import Home from './screens/Home';
import { NotFound, RouteError } from './screens/System';
import { TabBar, tabForPath } from './TabBar';

/**
 * The router, in data mode (createBrowserRouter + RouterProvider). Shell routes plus every
 * module route, each lazy. Two layouts:
 * - tabs: the thin header, the page column and the bottom tab bar.
 * - bare: full screen with safe-area padding only (keeper pad, board, live run, Lab play).
 *
 * There is no /install route: the gate is a render decision made before the router mounts.
 */

export interface RouteHandle {
  tab?: TabId;
  module?: string;
}

function Loading() {
  return (
    <div className="grid place-items-center py-16">
      <Spinner label={shellCopy.loading} />
    </div>
  );
}

function TabsLayout() {
  const matches = useMatches();
  const { pathname } = useLocation();
  const handle = matches.at(-1)?.handle as RouteHandle | undefined;
  const tab = handle?.tab ?? tabForPath(pathname);
  return (
    <div className="flex min-h-dvh flex-col bg-ground">
      <Header />
      <main className="tts-px-safe mx-auto w-full max-w-2xl flex-1 pt-5 pb-[calc(var(--tts-tabbar-h)+env(safe-area-inset-bottom,0px)+2rem)]">
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </main>
      <TabBar active={tab} />
      <ScrollRestoration />
    </div>
  );
}

function BareLayout() {
  return (
    <div className="tts-pt-safe tts-pb-safe tts-pl-safe tts-pr-safe min-h-dvh bg-ground">
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
    </div>
  );
}

function toRouteObject(r: ModuleRoute & { module: string }): RouteObject {
  return {
    path: r.path,
    handle: { tab: r.tab, module: r.module } satisfies RouteHandle,
    lazy: async () => ({ Component: (await r.screen()).default }),
  };
}

const shellRoute = (path: string, load: () => Promise<{ default: ComponentType }>, tab?: TabId): RouteObject => ({
  path,
  handle: { tab, module: 'shell' } satisfies RouteHandle,
  lazy: async () => ({ Component: (await load()).default }),
});

/** Shell-owned paths. Module routes must not reuse them (a registry test checks). */
export const SHELL_PATHS = ['/', '/play', '/me', '/settings', '/admin', '/j/:code', '*'] as const;

export function buildRoutes(): RouteObject[] {
  const mods = moduleRoutes();
  return [
    {
      element: <TabsLayout />,
      errorElement: <RouteError />,
      children: [
        { path: '/', element: <Home />, handle: { tab: 'home', module: 'shell' } satisfies RouteHandle },
        shellRoute('/play', () => import('./screens/PlayHub'), 'play'),
        shellRoute('/me', () => import('./screens/Me'), 'me'),
        shellRoute('/settings', () => import('./screens/Settings'), 'me'),
        shellRoute('/admin', () => import('./screens/Admin'), 'me'),
        // Signed in already: an invite link just lands on Home.
        { path: '/j/:code', element: <Navigate to="/" replace /> },
        ...mods.filter((r) => (r.layout ?? 'tabs') === 'tabs').map(toRouteObject),
        { path: '*', element: <NotFound /> },
      ],
    },
    {
      element: <BareLayout />,
      errorElement: <RouteError />,
      children: mods.filter((r) => r.layout === 'bare').map(toRouteObject),
    },
  ];
}

let router: ReturnType<typeof createBrowserRouter> | null = null;
export function appRouter(): ReturnType<typeof createBrowserRouter> {
  if (!router) router = createBrowserRouter(buildRoutes());
  return router;
}
